import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { ApplicationConfigService } from 'src/app/core/config/application-config.service';
import { CommonService } from 'src/app/shared/services/common.service';
import {
  IServiceEnrollmentFilter,
  IServiceEnrollmentRow,
  ServiceEnrollmentService,
} from 'src/app/admin/assisted-ekyc/shared/service-enrollment.service';
import { EkycListType, ServiceRequestStatus, ServiceRequestType } from 'src/app/admin/assisted-ekyc/shared/ekyc-request.enum';
import { IEkycRequestDetail } from 'src/app/admin/assisted-ekyc/shared/ekyc-request.model';

import { MockDb } from '../mock-db';

const MOCK_LATENCY_MS = 250;
const COLLECTION = 'ekycRequests';
const DEMO_DEBIT_CARD_ROWS: IServiceEnrollmentRow[] = [
  {
    serviceRequestId: 'SR-DC-20260724-001',
    ekycRequestId: 'EKYC-20260724-001',
    serviceType: ServiceRequestType.DEBIT_CARD,
    idempotencyKey: 'EKYC-20260724-001-DEBIT_CARD',
    status: ServiceRequestStatus.ACKNOWLEDGED,
    attemptCount: 1,
    attempts: [],
    deliveryBranchCode: '0101',
    deliveryBranchName: 'Motijheel Branch',
    cardProductTypeCode: 'CARD-VISA-CLASSIC',
    cardProductTypeName: 'Visa Classic Debit',
    nameOnCard: 'SELINA HOQUE',
    applicantName: 'Selina Hoque',
    nid: '19961158380',
    phoneNumber: '01710000620',
    accountNumber: '0101330012347',
    ekycType: 'SIMPLIFIED',
    branchName: 'Motijheel Branch',
    dateRequested: '2026-07-24T04:15:00.000Z',
  },
  {
    serviceRequestId: 'SR-DC-20260725-002',
    ekycRequestId: 'EKYC-20260725-002',
    serviceType: ServiceRequestType.DEBIT_CARD,
    idempotencyKey: 'EKYC-20260725-002-DEBIT_CARD',
    status: ServiceRequestStatus.PENDING,
    attemptCount: 0,
    attempts: [],
    deliveryBranchCode: '0120',
    deliveryBranchName: 'Gulshan Branch',
    cardProductTypeCode: 'CARD-MC-GOLD',
    cardProductTypeName: 'Mastercard Gold Debit',
    nameOnCard: 'ARIF MAHMUD',
    applicantName: 'Arif Mahmud',
    nid: '1989021456721',
    phoneNumber: '01819000455',
    accountNumber: '0120330045981',
    ekycType: 'REGULAR',
    branchName: 'Gulshan Branch',
    dateRequested: '2026-07-25T05:40:00.000Z',
  },
];

function readRequests(): IEkycRequestDetail[] {
  return MockDb.get<IEkycRequestDetail[]>(COLLECTION) ?? [];
}

function writeRequests(rows: IEkycRequestDetail[]): void {
  MockDb.set(COLLECTION, rows);
}

function envelope<T>(data: T) {
  return { responseCode: 'S100000', responseMessage: 'Operation successful', data };
}

function respond<T>(data: T): Observable<any> {
  return of(envelope(data)).pipe(delay(MOCK_LATENCY_MS));
}

function contains(value: unknown, filter: unknown): boolean {
  if (filter === null || filter === undefined || filter === '') return true;
  return String(value ?? '').toLowerCase().includes(String(filter).toLowerCase());
}

function equals(value: unknown, filter: unknown): boolean {
  if (filter === null || filter === undefined || filter === '') return true;
  return String(value ?? '') === String(filter);
}

/**
 * Service enrollment queues, projected out of the request store.
 *
 * Deliberately not a separate collection: a service request has no meaning apart from the
 * application that produced it, and keeping one source of truth means a status change here is
 * immediately visible on the parent request's detail page.
 */
@Injectable()
export class MockServiceEnrollmentService extends ServiceEnrollmentService {
  constructor(http: HttpClient, commonService: CommonService, appConfig: ApplicationConfigService) {
    super(http, commonService, appConfig);
  }

  /** Flattens every service request across every application into queue rows. */
  private rows(): IServiceEnrollmentRow[] {
    return DEMO_DEBIT_CARD_ROWS;
  }

  override fetchList(listType: EkycListType, query: IServiceEnrollmentFilter): Observable<any> {
    let rows = this.rows();

    rows = rows
      .filter((row) => contains(row.serviceRequestId, query.serviceRequestId))
      .filter((row) => equals(row.serviceType, query.serviceType))
      .filter((row) => contains(row.nid, query.nid))
      .filter((row) => contains(row.phoneNumber, query.mobileNumber))
      .filter((row) => contains(row.accountNumber, query.accountNumber))
      .filter((row) => equals(row.status, query.status))
      .sort((a, b) => b.dateRequested.localeCompare(a.dateRequested));

    const pageSize = Number(query.pageSize) || 10;
    const pageNumber = Number(query.pageNumber) || 0;
    const start = pageNumber * pageSize;

    return respond({
      data: rows.slice(start, start + pageSize),
      currentPage: pageNumber,
      pageSize,
      totalItems: rows.length,
    });
  }

  override details(serviceRequestId: string): Observable<any> {
    return respond(this.rows().find((row) => row.serviceRequestId === serviceRequestId) ?? null);
  }

  /** Mutates the service request inside its parent application. */
  private mutate(
    serviceRequestId: string,
    change: (service: any, request: IEkycRequestDetail) => any,
  ): Observable<any> {
    const requests = readRequests();
    let updated: any = null;

    const next = requests.map((request) => {
      if (!(request.serviceRequests ?? []).some((s) => s.serviceRequestId === serviceRequestId)) {
        return request;
      }
      return {
        ...request,
        serviceRequests: request.serviceRequests.map((service) => {
          if (service.serviceRequestId !== serviceRequestId) return service;
          updated = change(service, request);
          return updated;
        }),
      };
    });

    if (!updated) {
      return respond(null);
    }

    writeRequests(next);
    return respond(updated);
  }

  override retry(serviceRequestId: string): Observable<any> {
    const now = new Date().toISOString();
    return this.mutate(serviceRequestId, (service) => {
      const attemptNo = (service.attemptCount ?? 0) + 1;
      // A manual retry succeeds on the mock — the point is to show the exception clearing.
      return {
        ...service,
        status: ServiceRequestStatus.ACKNOWLEDGED,
        attemptCount: attemptNo,
        attempts: [
          ...(service.attempts ?? []),
          { attemptNo, at: now, outcome: 'ACK', reasonCode: null, rawResponse: '{"status":"accepted"}' },
        ],
      };
    });
  }

  override manuallyClose(serviceRequestId: string, reason: string): Observable<any> {
    const now = new Date().toISOString();
    return this.mutate(serviceRequestId, (service) => ({
      ...service,
      status: ServiceRequestStatus.MANUALLY_CLOSED,
      attempts: [
        ...(service.attempts ?? []),
        { attemptNo: (service.attemptCount ?? 0) + 1, at: now, outcome: 'MANUALLY_CLOSED', reasonCode: 'MANUAL', rawResponse: reason },
      ],
    }));
  }

  override updateStatus(serviceRequestId: string, status: ServiceRequestStatus, remarks: string): Observable<any> {
    const row = DEMO_DEBIT_CARD_ROWS.find((item) => item.serviceRequestId === serviceRequestId);
    if (!row) {
      return respond(null);
    }

    const attemptNo = (row.attemptCount ?? 0) + 1;
    row.status = status;
    row.attemptCount = attemptNo;
    row.attempts = [
      ...(row.attempts ?? []),
      { attemptNo, at: new Date().toISOString(), outcome: status, reasonCode: null, rawResponse: remarks },
    ];

    return respond(row);
  }

  /** Daily reconciliation per service type — SRS §M10. */
  override reconciliation(serviceType?: ServiceRequestType): Observable<any> {
    const rows = this.rows().filter((row) => (serviceType ? row.serviceType === serviceType : true));
    const count = (status: ServiceRequestStatus) => rows.filter((row) => row.status === status).length;

    return respond({
      requested: rows.length,
      acknowledged: count(ServiceRequestStatus.ACKNOWLEDGED),
      failed: count(ServiceRequestStatus.FAILED),
      retrying: count(ServiceRequestStatus.RETRY),
      manuallyClosed: count(ServiceRequestStatus.MANUALLY_CLOSED),
      pending: count(ServiceRequestStatus.PENDING) + count(ServiceRequestStatus.SUBMITTED),
    });
  }
}
